import { type Recommendation, recommendationsOf } from './recommendationRows.js';
import { asRecord, joined, text } from './resultValues.js';

// A brief run returns the creative rows it was written from, each carrying its
// own brief — one list, not a brief beside an echo of the row.
const BRIEFS = 'creatives';

export interface BriefSetting {
  label: string;
  // Null where the run carried nothing: the field is shown unset rather than
  // dropped, so a reader can see what a brief is cut under and what it is not.
  value: string | null;
}

export interface Brief {
  creativeId: string;
  name: string;
  body: string;
  settings: BriefSetting[];
  preferences: BriefSetting[];
  recommendations: Recommendation[];
}

// The five the design lays out, in its order. `Scene focused` and `Placement`
// have no field on the run yet — scene focus is tracked in the actions repo —
// so they read as unset rather than being left out of the row.
function settingsOf(brief: Record<string, unknown>): BriefSetting[] {
  return [
    { label: 'Campaign objective', value: joined(brief['campaign_objectives']) },
    { label: 'Scene focused', value: null },
    { label: 'Concept', value: joined(brief['concepts']) },
    { label: 'Placement', value: null },
    { label: 'KPI to improve', value: text(brief['kpi_label']) ?? text(brief['kpi']) },
  ];
}

function preferencesOf(brief: Record<string, unknown>): BriefSetting[] {
  return [
    { label: 'Video ratio', value: joined(brief['formats']) },
    { label: 'Duration', value: joined(brief['durations']) },
    { label: 'Locations', value: null },
  ];
}

export function briefsOf(result: unknown): Brief[] {
  const holder = asRecord(result);
  const briefs = holder === null ? null : holder[BRIEFS];
  if (!Array.isArray(briefs)) return [];
  return briefs.flatMap((entry) => {
    const brief = asRecord(entry);
    const creativeId = brief === null ? null : text(brief['creative_id']);
    if (brief === null || creativeId === null) return [];
    return [
      {
        creativeId,
        name: text(brief['name']) ?? 'Brief',
        body: text(brief['body']) ?? '',
        settings: settingsOf(brief),
        preferences: preferencesOf(brief),
        recommendations: recommendationsOf(brief),
      },
    ];
  });
}
